import ApplicationController from './application_controller.js'
import CfileRiskFormAPI from './cfile_risk_form/cfile_risk_form_api.js'
import RiskFormRenderer from './risk_form/risk_form_renderer.js'
import RiskFormUtils from './risk_form/risk_form_utils.js'

export default class CfileRiskFormController extends ApplicationController {
  static targets = [
    "accessButton", 
    "questionsContainer", 
    "questionsForm", 
    "existingAnswersContainer", 
    "existingAnswersContent", 
    "riskSelectionContainer", 
    "editAnswersButton", 
    "selectedRiskLevelDisplay",
    "exportPdfButton",
    "deleteButton"
  ]

  // Vérifier si les boutons optionnels sont présents
  get hasEditButton() {
    return this.hasEditAnswersButtonTarget
  }

  get hasExportButton() {
    return this.hasExportPdfButtonTarget
  }

  connect() {
    this.api = new CfileRiskFormAPI(RiskFormUtils.getCfileId())
    this.renderer = new RiskFormRenderer()
    this.state = {
      firstRiskLevel: null,
      existingAnswersData: null
    }
    
    this.initialize()
  }

  // Initialisation
  async initialize() {
    try {
      // Attendre que l'API soit initialisée
      if (this.api) {
        await this.restoreLastSelectedRiskLevel()
        await this.checkExistingAnswers()
        // checkExistingAnswers() décide automatiquement de l'affichage
        // Si des réponses existent, elles seront affichées
        // Sinon, la sélection du niveau de risque sera affichée
      }
    } catch (error) {
      console.log('Erreur lors de l\'initialisation:', error)
      this.showRiskSelection()
    }
  }

  // Restaurer le dernier niveau de risque sélectionné
  async restoreLastSelectedRiskLevel() {
    try {
      const riskLevelId = await this.api.getLastRiskLevel()
      if (riskLevelId) {
        const select = document.getElementById('risk_level_select')
        if (RiskFormUtils.setSelectValue(select, riskLevelId)) {
          this.updateAccessButton()
        }
      }
    } catch (error) {
      console.log('Aucun niveau de risque sauvegardé ou erreur:', error)
    }
  }

  // Gestion du changement de niveau de risque
  riskLevelChanged(event) {
    this.updateAccessButton()
  }

  // Mettre à jour le bouton d'accès
  updateAccessButton() {
    const selectedValue = RiskFormUtils.getSelectedRiskLevel()
    const accessButton = this.accessButtonTarget
    
    if (selectedValue) {
      const hasRiskLevelChanged = this.state.firstRiskLevel && 
        this.state.firstRiskLevel.id.toString() !== selectedValue
      
      if (this.state.firstRiskLevel && !hasRiskLevelChanged) {
        this.hideAccessButton()
      } else {
        this.showAccessButton(hasRiskLevelChanged)
      }
    } else {
      this.hideAccessButton()
      this.hideQuestionsForm()
    }
  }

  showAccessButton(isRegenerate = false) {
    const accessButton = this.accessButtonTarget
    const buttonText = document.getElementById('access_button_text')
    
    accessButton.style.display = 'inline-block'
    accessButton.disabled = false
    buttonText.textContent = isRegenerate ? 'Régénérer le formulaire' : 'Générer le formulaire'
  }

  hideAccessButton() {
    const accessButton = this.accessButtonTarget
    accessButton.style.display = 'none'
    accessButton.disabled = true
  }

  // Accéder aux questions
  async accessQuestions(event) {
    event.preventDefault()
    
    const riskLevelId = RiskFormUtils.getSelectedRiskLevel()
    if (!riskLevelId) return

    try {
      this.showLoading()
      const questions = await this.api.getQuestions(riskLevelId)
      this.hideLoading()
      
      // Mémoriser le niveau de risque utilisé pour cette session d'édition
      this.state.currentRiskLevelId = riskLevelId

      // Bascule en mode édition: masquer le mode show et la sélection
      if (this.hasEditButton) {
        this.editAnswersButtonTarget.style.display = 'none'
      }
      if (this.hasExportButton) {
        this.exportPdfButtonTarget.style.display = 'none'
      }
      this.existingAnswersContainerTarget.style.display = 'none'
      this.riskSelectionContainerTarget.style.display = 'block'
      
      const hasExistingAnswers = questions.some(q => q.existing_answer)
      if (hasExistingAnswers) {
        this.hideAccessButton()
      }
      
      this.displayQuestions(questions)
    } catch (error) {
      this.hideLoading()
      this.displayError('Aucune question trouvée pour ce niveau de risque.')
    }
  }

  // Afficher les questions
  displayQuestions(questions) {
    const container = this.questionsContainerTarget
    const form = this.questionsFormTarget

    form.innerHTML = this.renderer.renderQuestionsForm(questions)
    
    // Ajouter l'écouteur d'événement pour la soumission
    const formElement = document.getElementById('risk_questions_form')
    if (formElement) {
      formElement.addEventListener('submit', (e) => this.submitAnswers(e))
    }

    container.style.display = 'block'
  }

  // Afficher une erreur dans le partial
  displayError(message) {
    const container = this.questionsContainerTarget
    const form = this.questionsFormTarget

    form.innerHTML = `<div class="alert alert-error" style="color: #721c24; background-color: #f8f9fa; border: 1px solid #dee2e6; padding: 0.75rem; border-radius: 4px; margin-bottom: 1rem;">${message}</div>`
    container.style.display = 'block'
  }

  // Soumettre les réponses
  async submitAnswers(event) {
    event.preventDefault()
    
    try {
      this.showLoading()
      const formData = RiskFormUtils.createFormData(event.target)
      
      // Ajouter le risk_level_id aux données du formulaire
      const riskLevelId = RiskFormUtils.getSelectedRiskLevel()
      if (riskLevelId) {
        formData.append('risk_level_id', riskLevelId)
      }
      
      const success = await this.api.saveAnswers(formData)
      
      if (success) {
        this.hideQuestionsForm()
        // Réafficher en mode show en privilégiant le niveau de risque courant
        const levelToShow = this.state.currentRiskLevelId || riskLevelId || null
        await this.checkExistingAnswers(levelToShow)
      } else {
        this.displayError('Erreur lors de l\'enregistrement')
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
      this.displayError(error.message || 'Erreur lors de la sauvegarde')
    } finally {
      this.hideLoading()
    }
  }

  // Vérifier les réponses existantes
  async checkExistingAnswers(riskLevelId = null) {
    try {
      const riskLevelToCheck = riskLevelId || RiskFormUtils.getSelectedRiskLevel()
      console.log('🔍 checkExistingAnswers appelé avec riskLevelId:', riskLevelToCheck)
      
      const { hasAnswers, answersByRiskLevel, firstRiskLevel } = await this.api.getExistingAnswers(riskLevelToCheck)
      
      console.log('🔍 Réponse de getExistingAnswers:', { hasAnswers, answersByRiskLevel, firstRiskLevel })
      
      this.state.firstRiskLevel = firstRiskLevel
      this.state.existingAnswersData = answersByRiskLevel
      
      console.log('🔍 État mis à jour:', this.state)
      
      if (hasAnswers) {
        this.displayExistingAnswers(answersByRiskLevel, firstRiskLevel, riskLevelToCheck)
      } else {
        // Aucune réponse existante : pré-remplir avec le niveau de risque du domaine
        await this.showRiskSelectionWithDomainDefault()
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des réponses:', error)
      await this.showRiskSelectionWithDomainDefault()
    }
  }

  // Afficher les réponses existantes
  displayExistingAnswers(answersByRiskLevel, firstRiskLevel, currentRiskLevelId) {
    const container = this.existingAnswersContainerTarget
    const content = this.existingAnswersContentTarget
    const riskLevelDisplay = this.selectedRiskLevelDisplayTarget

    const result = this.renderer.renderExistingAnswers(answersByRiskLevel, firstRiskLevel, currentRiskLevelId)
    
    if (result.riskLevelName) {
      riskLevelDisplay.innerHTML = `<div style="color: #96254E; padding: 0.5rem 0;">Niveau de risque du dossier : ${result.riskLevelName}</div>`
    }

    content.innerHTML = result.html
    container.style.display = 'block'
    this.riskSelectionContainerTarget.style.display = 'none'
    
    // Afficher les boutons en mode show
    if (this.hasEditButton) {
      this.editAnswersButtonTarget.style.display = 'inline-block'
    }
    if (this.hasDeleteButton) {
      this.deleteButtonTarget.style.display = 'inline-block'
    }
    if (this.hasExportButton) {
      this.exportPdfButtonTarget.style.display = 'inline-block'
    }
  }

  // Passer en mode édition
  async showEditMode() {
    console.log('🔧 showEditMode appelé (dossier)')
    console.log('🔍 État actuel:', this.state)
    
    this.existingAnswersContainerTarget.style.display = 'none'
    this.showRiskSelection()
    
    if (this.hasEditButton) {
      this.editAnswersButtonTarget.style.display = 'none'
    }
    
    if (this.hasDeleteButton) {
      this.deleteButtonTarget.style.display = 'none'
    }
    
    if (this.hasExportButton) {
      this.exportPdfButtonTarget.style.display = 'none'
    }
    
    const select = document.getElementById('risk_level_select')
    let riskLevelToUse = null
    
    console.log('🔍 Select element:', select)
    console.log('🔍 firstRiskLevel:', this.state.firstRiskLevel)
    console.log('🔍 existingAnswersData:', this.state.existingAnswersData)
    
    // 1) D'abord, essayer de récupérer le niveau de risque actuellement sélectionné dans le select
    if (select && select.value) {
      console.log('🔍 Niveau de risque actuellement sélectionné dans le select:', select.value)
      riskLevelToUse = { id: select.value }
    }
    
    // 2) Si pas de sélection dans le select, essayer de récupérer depuis les données existantes
    if (!riskLevelToUse && this.state.existingAnswersData) {
      const selectedRiskLevel = this.state.existingAnswersData.find(riskLevelData => 
        riskLevelData.risk_level && riskLevelData.risk_level.id
      )
      if (selectedRiskLevel) {
        riskLevelToUse = selectedRiskLevel.risk_level
        console.log('🔍 Niveau de risque trouvé dans les données existantes:', selectedRiskLevel)
      }
    }
    
    // 3) En dernier recours, utiliser firstRiskLevel (mais seulement s'il a un ID valide)
    if (!riskLevelToUse && this.state.firstRiskLevel && this.state.firstRiskLevel.id) {
      riskLevelToUse = this.state.firstRiskLevel
      console.log('🔍 Utilisation de firstRiskLevel comme fallback:', this.state.firstRiskLevel)
    }
    
    if (riskLevelToUse && riskLevelToUse.id) {
      console.log('✅ Chargement des questions avec le niveau de risque:', riskLevelToUse.id)
      RiskFormUtils.setSelectValue(select, riskLevelToUse.id)
      this.loadQuestionsWithExistingAnswers(riskLevelToUse.id)
    } else {
      console.log('⚠️ Aucun niveau de risque défini, tentative de pré-remplir avec le domaine')
      // Si aucun niveau de risque n'est défini, essayer de pré-remplir avec le domaine
      try {
        const defaultRiskLevelId = await this.api.getLastRiskLevel()
        if (defaultRiskLevelId) {
          console.log('✅ Niveau de risque du domaine trouvé:', defaultRiskLevelId)
          RiskFormUtils.setSelectValue(select, defaultRiskLevelId)
          this.updateAccessButton()
        } else {
          console.log('⚠️ Aucun niveau de risque du domaine, affichage du bouton de génération')
          this.showAccessButton(false)
          this.hideQuestionsForm()
        }
      } catch (error) {
        console.log('⚠️ Erreur lors de la récupération du niveau de risque du domaine:', error)
        this.showAccessButton(false)
        this.hideQuestionsForm()
      }
      
      console.log('✅ showEditMode terminé')
    }
  }

  // Exporter en PDF
  exportPdf() {
    const riskLevelId = RiskFormUtils.getSelectedRiskLevel()
    if (!riskLevelId) {
      this.displayError('Aucun niveau de risque sélectionné')
      return
    }
    
    const cfileId = RiskFormUtils.getCfileId()
    const url = `/cfiles/${cfileId}/risk_form_pdf.pdf?risk_level_id=${riskLevelId}`
    
    // Ouvrir le PDF dans un nouvel onglet
    window.open(url, '_blank')
  }

  // Afficher la sélection du niveau de risque
  showRiskSelection() {
    this.riskSelectionContainerTarget.style.display = 'block'
    this.existingAnswersContainerTarget.style.display = 'none'
  }

  // Afficher la sélection avec le niveau de risque du domaine pré-rempli
  async showRiskSelectionWithDomainDefault() {
    this.showRiskSelection()
    
    // Récupérer le niveau de risque par défaut (domaine ou dernier utilisé)
    try {
      const defaultRiskLevelId = await this.api.getLastRiskLevel()
      if (defaultRiskLevelId) {
        const select = document.getElementById('risk_level_select')
        if (select) {
          RiskFormUtils.setSelectValue(select, defaultRiskLevelId)
          this.updateAccessButton()
          console.log('✅ Niveau de risque du domaine pré-rempli:', defaultRiskLevelId)
        }
      }
    } catch (error) {
      console.log('Aucun niveau de risque par défaut disponible:', error)
    }
  }

  // Charger les questions avec réponses existantes
  async loadQuestionsWithExistingAnswers(riskLevelId = null) {
    const levelId = riskLevelId || (this.state.firstRiskLevel ? this.state.firstRiskLevel.id : null)
    if (!levelId) return
    
    try {
      this.showLoading()
      const questions = await this.api.getQuestions(levelId)
      this.hideLoading()
      
      this.hideAccessButton()
      this.displayQuestions(questions)
    } catch (error) {
      this.hideLoading()
      this.displayError('Aucune question trouvée pour ce niveau de risque.')
    }
  }

  // Gestion de l'interface
  hideQuestionsForm() {
    this.questionsContainerTarget.style.display = 'none'
    this.questionsFormTarget.innerHTML = ''
  }

  hideExistingAnswers() {
    this.existingAnswersContainerTarget.style.display = 'none'
  }

  showLoading() {
    this.questionsFormTarget.innerHTML = this.renderer.renderLoading()
  }

  hideLoading() {
    // L'indicateur de chargement est remplacé par le contenu
  }

  // Supprimer la fiche de risque
  async deleteRiskForm() {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette fiche de risque ? Toutes les réponses seront effacées.')) {
      return
    }

    try {
      const riskLevelId = RiskFormUtils.getSelectedRiskLevel() // Peut être null pour supprimer toutes les réponses
      const success = await this.api.deleteRiskForm(riskLevelId)

      if (success) {
        RiskFormUtils.showSuccess('Fiche de risque supprimée avec succès.')
        this.hideQuestionsForm()
        this.hideExistingAnswers()
        this.showRiskSelection()
        this.hideDeleteButton()
        this.state.firstRiskLevel = null // Réinitialiser l'état
        this.state.existingAnswersData = null
        this.updateAccessButton() // Mettre à jour l'état du bouton "Générer"
      } else {
        RiskFormUtils.showError('Erreur lors de la suppression de la fiche de risque.')
      }
    } catch (error) {
      RiskFormUtils.handleError(error, 'suppression de la fiche de risque')
    }
  }

  hideDeleteButton() {
    if (this.hasDeleteButton) {
      this.deleteButtonTarget.style.display = 'none'
    }
  }

  get hasDeleteButton() {
    return this.hasDeleteButtonTarget
  }
}